import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { useAuth } from './AuthContext';
import Login from './Login';
import { api, downloadExcel } from './api';
import { Ikon } from './Ikon';
import { TimelineModal } from './TimelineModal';
import { OzetKartSkeleton, UrunKartSkeleton, TabloSatirSkeleton } from './SkeletonLoaders';
import './App.css';

// ── PDF Yükleyici ─────────────────────────────────────────────────────────────
function PdfYukle({ value, onDegis, onKaldir, inputId }) {
  const handleDosya = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('PDF en fazla 5 MB olabilir!'); return; }
    const form = new FormData();
    form.append('dosya', file);
    try {
      const res = await api.upload('/api/upload/pdf', form);
      onDegis({ ad: res.dosya_adi, yol: res.fatura_yolu });
    } catch (err) { alert('Yükleme hatası: ' + err.message); }
    e.target.value = '';
  };
  return (
    <div className="dosya-yukle-kap">
      <input type="file" accept=".pdf" id={inputId} style={{ display: 'none' }} onChange={handleDosya} />
      {value ? (
        <div className="secilen-dosya">
          <button type="button" className="dosya-goruntule-btn" onClick={() => window.open(api.staticUrl(value.yol))}>
            <Ikon ad="belge" boyut={14} /> {value.ad}
          </button>
          <button type="button" className="dosya-kaldir" onClick={onKaldir}><Ikon ad="kapat" boyut={13} /></button>
        </div>
      ) : (
        <label htmlFor={inputId} className="dosya-ekle-btn"><Ikon ad="atas" boyut={14} /> PDF Fatura Ekle</label>
      )}
    </div>
  );
}

// ── Grafik Yardımcıları ───────────────────────────────────────────────────────
// Grafik renkleri — koyu yüzey için validatörden geçirilmiş set
const PASTA_RENKLER = ['#3987E5', '#199E70', '#C98500', '#9085E9', '#D55181', '#D95926'];

const TOOLTIP_STIL = {
  contentStyle: { background: '#131B24', border: '1px solid #33475C', borderRadius: 8, color: '#E7EEF5' },
  labelStyle: { color: '#9AABBC' },
  itemStyle: { color: '#E7EEF5' },
};

function aylikVeri(islemler) {
  const aylar = {};
  const simdi = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(simdi.getFullYear(), simdi.getMonth() - i, 1);
    const key = d.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
    aylar[key] = { ay: key, giris: 0, cikis: 0 };
  }
  islemler.forEach(i => {
    const d = new Date(i.tarih);
    const key = d.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
    if (aylar[key]) aylar[key][i.tip]++;
  });
  return Object.values(aylar);
}

function enCokTuketilen(islemler) {
  const sayac = {};
  islemler.filter(i => i.tip === 'cikis').forEach(i => {
    sayac[i.urun_ad] = (sayac[i.urun_ad] || 0) + i.miktar;
  });
  return Object.entries(sayac)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([ad, miktar]) => ({ ad, miktar }));
}

function kisiDagilimi(islemler) {
  const sayac = {};
  islemler.filter(i => i.tip === 'cikis').forEach(i => {
    sayac[i.kullanici_ad] = (sayac[i.kullanici_ad] || 0) + 1;
  });
  return Object.entries(sayac).map(([ad, deger]) => ({ ad, deger }));
}

// ── Kategori Alanı ────────────────────────────────────────────────────────────
// Dashboard'un DIŞINDA tanımlı olmalı: içeride tanımlanırsa her state
// değişiminde bileşen yeniden yaratılır, input DOM'dan söküp takılır ve
// autoFocus diğer alanlardan odağı çalar.
function KategoriAlani({ kategoriler, yeniUrun, setYeniUrun, yeniKatModu, setYeniKatModu }) {
  if (kategoriler.length === 0) {
    return (
      <div className="form-grup">
        <label>Kategori</label>
        <input placeholder="Örn: Elektronik, Sarf Malzeme..." value={yeniUrun.yeni_kategori} onChange={e => { setYeniKatModu(true); setYeniUrun(p => ({ ...p, yeni_kategori: e.target.value })); }} />
        <span className="form-ipucu"><Ikon ad="arti" boyut={11} /> Sistemde henüz kategori yok — yazdığınız isim yeni bir kategori olarak oluşturulup bu ürüne bağlanacak.</span>
      </div>
    );
  }
  return (
    <div className="form-grup">
      <label>Kategori</label>
      <select
        value={yeniKatModu ? '__yeni__' : (yeniUrun.kategori_id || '')}
        onChange={e => {
          if (e.target.value === '__yeni__') { setYeniKatModu(true); setYeniUrun(p => ({ ...p, kategori_id: '', yeni_kategori: '' })); }
          else { setYeniKatModu(false); setYeniUrun(p => ({ ...p, kategori_id: e.target.value, yeni_kategori: '' })); }
        }}
      >
        <option value="">— Kategori Seçin —</option>
        {kategoriler.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
        <option value="__yeni__">+ Yeni kategori ekle</option>
      </select>
      {yeniKatModu && (
        <input placeholder="Yeni kategori adını yazın..." value={yeniUrun.yeni_kategori} onChange={e => setYeniUrun(p => ({ ...p, yeni_kategori: e.target.value }))} autoFocus />
      )}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard() {
  const { aktifKullanici, cikisYap, kullaniciEkle, kullaniciSil, sifreGuncelle, rolGuncelle, kullanicilariGetir } = useAuth();

  const [urunler, setUrunler]       = useState([]);
  const [islemler, setIslemler]     = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  const [aktifSekme, setAktifSekme]   = useState('panel');
  const [yeniKatModu, setYeniKatModu] = useState(false);

  // Modal state'leri
  const [urunModal, setUrunModal]     = useState(false);
  const [cikisModal, setCikisModal]   = useState(false);
  const [girisModal, setGirisModal]   = useState(false);
  const [kullaniciModal, setKullaniciModal] = useState(false);
  const [sifreModal, setSifreModal]   = useState(null);
  const [silinecekUrun, setSilinecekUrun] = useState(null);
  const [duzenlenecekUrun, setDuzenlenecekUrun] = useState(null); // { id, ad, kategori_id, yeni_kategori }
  const [duzenleKatModu, setDuzenleKatModu] = useState(false);
  const [silinecekKullanici, setSilinecekKullanici] = useState(null);
  const [rolDegisecek, setRolDegisecek] = useState(null);
  const [kategoriModal, setKategoriModal] = useState(false);
  const [silinecekKategori, setSilinecekKategori] = useState(null);
  const [duplikatUyari, setDuplikatUyari] = useState(null); // { mesaj, onay: fn }

  // Form state'leri
  const [yeniUrun, setYeniUrun]   = useState({ ad: '', kategori_id: '', yeni_kategori: '', miktar: '', birim: 'adet', kritik_seviye: '' });
  const [urunFatura, setUrunFatura] = useState(null);
  const [yeniCikis, setYeniCikis] = useState({ urun_id: '', kategori_id: '', miktar: '', not_: '' });
  const [yeniGiris, setYeniGiris] = useState({ urun_id: '', kategori_id: '', miktar: '', not_: '' });
  const [girisFatura, setGirisFatura] = useState(null);
  const [yeniKullanici, setYeniKullanici] = useState({ kullaniciAdi: '', sifre: '', ad: '', rol: 'kullanici' });
  const [yeniSifre, setYeniSifre] = useState('');
  const [yeniKategoriAdi, setYeniKategoriAdi] = useState('');

  // Filtre state'leri
  const [aramaMetni, setAramaMetni]   = useState('');
  const [islemArama, setIslemArama]   = useState('');
  const [islemTip, setIslemTip]       = useState('hepsi');
  const [baslangic, setBaslangic]     = useState('');
  const [bitis, setBitis]             = useState('');

  // Tema ve Modal state'leri
  const [tema, setTema] = useState(() => localStorage.getItem('stok_tema') || 'dark');
  const [timelineUrun, setTimelineUrun] = useState(null);

  // Sayfalama state'leri
  const [stokSayfa, setStokSayfa] = useState(1);
  const [islemSayfa, setIslemSayfa] = useState(1);
  const SAYFA_BOYUTU = 10;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', tema);
    localStorage.setItem('stok_tema', tema);
  }, [tema]);

  const [bildirim, setBildirim] = useState(null);

  // ── Veri yükleme ──────────────────────────────────────────────────────────
  const verileriYukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const params = new URLSearchParams();
      if (islemTip !== 'hepsi') params.append('tip', islemTip);
      if (baslangic) params.append('baslangic', baslangic);
      if (bitis) params.append('bitis', bitis);
      if (islemArama) params.append('arama', islemArama);

      const [u, i, k] = await Promise.all([
        api.get('/api/urunler'),
        api.get('/api/islemler?' + params.toString()),
        api.get('/api/kategoriler'),
      ]);
      setUrunler(u);
      setIslemler(i);
      setKategoriler(k);
    } catch (e) { bildirimGoster(e.message, 'hata'); }
    finally { setYukleniyor(false); }
  }, [islemTip, baslangic, bitis, islemArama]);

  useEffect(() => { verileriYukle(); }, [verileriYukle]);

  useEffect(() => {
    if (aktifKullanici.rol === 'admin') {
      kullanicilariGetir().then(setKullanicilar).catch(() => {});
    }
  }, []);

  const bildirimGoster = (mesaj, tur = 'basari') => {
    setBildirim({ mesaj, tur });
    setTimeout(() => setBildirim(null), 3500);
  };

  // ── Modal kapat yardımcıları ───────────────────────────────────────────────
  const urunModalKapat  = () => { setUrunModal(false);  setUrunFatura(null);  setYeniKatModu(false); setYeniUrun({ ad: '', kategori_id: '', yeni_kategori: '', miktar: '', birim: 'adet', kritik_seviye: '' }); };
  const girisModalKapat = () => { setGirisModal(false); setGirisFatura(null); setYeniGiris({ urun_id: '', kategori_id: '', miktar: '', not_: '' }); };
  const cikisModalKapat = () => { setCikisModal(false); setYeniCikis({ urun_id: '', kategori_id: '', miktar: '', not_: '' }); };

  // ── İşlemler ──────────────────────────────────────────────────────────────
  const urunEkle = async () => {
    if (!yeniUrun.ad.trim() || !yeniUrun.miktar) { bildirimGoster('Ürün adı ve miktar zorunludur!', 'hata'); return; }
    try {
      let kat_id = yeniUrun.kategori_id ? parseInt(yeniUrun.kategori_id) : null;
      if (yeniKatModu && yeniUrun.yeni_kategori.trim()) {
        const kat = await api.post('/api/kategoriler', { ad: yeniUrun.yeni_kategori.trim() });
        kat_id = kat.id;
        setKategoriler(prev => [...prev.filter(k => k.id !== kat.id), kat].sort((a, b) => a.ad.localeCompare(b.ad)));
      }
      await api.post('/api/urunler', {
        ad: yeniUrun.ad.trim(),
        kategori_id: kat_id,
        miktar: parseInt(yeniUrun.miktar),
        birim: yeniUrun.birim,
        kritik_seviye: parseInt(yeniUrun.kritik_seviye) || 5,
        fatura_yolu: urunFatura?.yol || null,
      });
      urunModalKapat();
      verileriYukle();
      bildirimGoster(`"${yeniUrun.ad}" stoka eklendi`);
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const stokGiris = async (force = false) => {
    if (!yeniGiris.urun_id || !yeniGiris.miktar) { bildirimGoster('Ürün ve miktar seçilmelidir!', 'hata'); return; }
    try {
      const urun = urunler.find(u => u.id === parseInt(yeniGiris.urun_id));
      await api.post('/api/stok/giris', { urun_id: parseInt(yeniGiris.urun_id), miktar: parseInt(yeniGiris.miktar), not_: yeniGiris.not_, fatura_yolu: girisFatura?.yol || null, force });
      setDuplikatUyari(null);
      girisModalKapat();
      verileriYukle();
      bildirimGoster(`${urun?.ad} stoka eklendi`);
    } catch (e) {
      if (e.isDuplicate) {
        setDuplikatUyari({ mesaj: e.message, onay: () => stokGiris(true) });
      } else {
        bildirimGoster(e.message, 'hata');
      }
    }
  };

  const stokCikis = async (force = false) => {
    if (!yeniCikis.urun_id || !yeniCikis.miktar || !yeniCikis.not_?.trim()) {
      bildirimGoster('Ürün, miktar ve çıkış sebebi zorunludur!', 'hata');
      return;
    }
    try {
      const urun = urunler.find(u => u.id === parseInt(yeniCikis.urun_id));
      await api.post('/api/stok/cikis', { urun_id: parseInt(yeniCikis.urun_id), miktar: parseInt(yeniCikis.miktar), not_: yeniCikis.not_, force });
      setDuplikatUyari(null);
      cikisModalKapat();
      verileriYukle();
      bildirimGoster(`${aktifKullanici.ad} → ${urun?.ad}`);
    } catch (e) {
      if (e.isDuplicate) {
        setDuplikatUyari({ mesaj: e.message, onay: () => stokCikis(true) });
      } else {
        bildirimGoster(e.message, 'hata');
      }
    }
  };

  const urunSil = async (id) => {
    try {
      await api.del(`/api/urunler/${id}`);
      setSilinecekUrun(null);
      verileriYukle();
      bildirimGoster('Ürün silindi', 'uyari');
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const duzenlemeAc = (u) => {
    setDuzenlenecekUrun({ id: u.id, ad: u.ad, kategori_id: u.kategori_id || '', yeni_kategori: '' });
    setDuzenleKatModu(false);
  };
  const duzenlemeKapat = () => { setDuzenlenecekUrun(null); setDuzenleKatModu(false); };

  const urunGuncelle = async () => {
    if (!duzenlenecekUrun.ad.trim()) { bildirimGoster('Ürün adı zorunludur!', 'hata'); return; }
    try {
      let kat_id = duzenlenecekUrun.kategori_id ? parseInt(duzenlenecekUrun.kategori_id) : null;
      if (duzenleKatModu && duzenlenecekUrun.yeni_kategori.trim()) {
        const kat = await api.post('/api/kategoriler', { ad: duzenlenecekUrun.yeni_kategori.trim() });
        kat_id = kat.id;
        setKategoriler(prev => [...prev.filter(k => k.id !== kat.id), kat].sort((a, b) => a.ad.localeCompare(b.ad)));
      }
      await api.put(`/api/urunler/${duzenlenecekUrun.id}`, { ad: duzenlenecekUrun.ad.trim(), kategori_id: kat_id });
      duzenlemeKapat();
      verileriYukle();
      bildirimGoster('Ürün güncellendi');
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const handleKullaniciEkle = async () => {
    if (!yeniKullanici.kullaniciAdi.trim() || !yeniKullanici.sifre || !yeniKullanici.ad.trim()) { bildirimGoster('Tüm alanları doldurun!', 'hata'); return; }
    try {
      await kullaniciEkle(yeniKullanici);
      const liste = await kullanicilariGetir();
      setKullanicilar(liste);
      setYeniKullanici({ kullaniciAdi: '', sifre: '', ad: '', rol: 'kullanici' });
      setKullaniciModal(false);
      bildirimGoster(`"${yeniKullanici.ad}" eklendi`);
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const handleSifreGuncelle = async () => {
    if (!yeniSifre.trim()) { bildirimGoster('Şifre boş olamaz!', 'hata'); return; }
    try {
      await sifreGuncelle(sifreModal.id, yeniSifre.trim());
      setSifreModal(null); setYeniSifre('');
      bildirimGoster('Şifre güncellendi');
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const handleRolDegistir = async () => {
    const yeniRol = rolDegisecek.rol === 'admin' ? 'kullanici' : 'admin';
    try {
      await rolGuncelle(rolDegisecek.id, yeniRol);
      const liste = await kullanicilariGetir();
      setKullanicilar(liste);
      setRolDegisecek(null);
      bildirimGoster(`"${rolDegisecek.ad}" artık ${yeniRol === 'admin' ? 'Yönetici' : 'Kullanıcı'}`);
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const handleKullaniciSil = async (id) => {
    try {
      await kullaniciSil(id);
      const liste = await kullanicilariGetir();
      setKullanicilar(liste);
      setSilinecekKullanici(null);
      bildirimGoster('Kullanıcı silindi', 'uyari');
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const kategoriModalKapat = () => { setKategoriModal(false); setYeniKategoriAdi(''); };

  const kategoriEkle = async () => {
    if (!yeniKategoriAdi.trim()) { bildirimGoster('Kategori adı zorunludur!', 'hata'); return; }
    try {
      const kat = await api.post('/api/kategoriler', { ad: yeniKategoriAdi.trim() });
      setKategoriler(prev => [...prev.filter(k => k.id !== kat.id), kat].sort((a, b) => a.ad.localeCompare(b.ad)));
      kategoriModalKapat();
      bildirimGoster(`"${kat.ad}" kategorisi eklendi`);
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const kategoriSil = async (id) => {
    try {
      await api.del(`/api/kategoriler/${id}`);
      setSilinecekKategori(null);
      verileriYukle();
      bildirimGoster('Kategori silindi', 'uyari');
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const handleExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (baslangic) params.append('baslangic', baslangic);
      if (bitis) params.append('bitis', bitis);
      await downloadExcel('?' + params.toString());
      bildirimGoster('Excel indirildi');
    } catch (e) { bildirimGoster(e.message, 'hata'); }
  };

  const hizliTarih = (tip) => {
    const bugun = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (tip === 'hafta') { const d = new Date(bugun); d.setDate(d.getDate() - 7); setBaslangic(fmt(d)); setBitis(fmt(bugun)); }
    else if (tip === 'ay') { const d = new Date(bugun); d.setMonth(d.getMonth() - 1); setBaslangic(fmt(d)); setBitis(fmt(bugun)); }
    else if (tip === '3ay') { const d = new Date(bugun); d.setMonth(d.getMonth() - 3); setBaslangic(fmt(d)); setBitis(fmt(bugun)); }
    else { setBaslangic(''); setBitis(''); }
  };

  const tarihFormat = (iso) => new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const kritikUrunler = urunler.filter(u => u.miktar <= u.kritik_seviye);
  const bugunIslem    = islemler.filter(i => new Date(i.tarih).toDateString() === new Date().toDateString()).length;
  const benimIslemlerim = islemler.filter(i => i.kullanici_id === aktifKullanici.id).length;

  const filtreliUrunler = urunler.filter(u =>
    u.ad.toLowerCase().includes(aramaMetni.toLowerCase()) ||
    (u.kategori_ad || '').toLowerCase().includes(aramaMetni.toLowerCase())
  );

  const urunKategorileri = [...new Set(filtreliUrunler.map(u => u.kategori_ad || 'Genel'))];

  const acikGirisModal = (urunId = '') => {
    const secilen = urunler.find(u => u.id === parseInt(urunId));
    setYeniGiris({ urun_id: String(urunId), kategori_id: secilen?.kategori_id ? String(secilen.kategori_id) : '', miktar: '', not_: '' });
    setGirisFatura(null);
    setGirisModal(true);
  };
  const acikCikisModal = (urunId = '') => {
    const secilen = urunler.find(u => u.id === parseInt(urunId));
    setYeniCikis({ urun_id: String(urunId), kategori_id: secilen?.kategori_id ? String(secilen.kategori_id) : '', miktar: '', not_: '' });
    setCikisModal(true);
  };

  return (
    <div className="app">
      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="logo">
          <span className="logo-ikon"><Ikon ad="kutu" boyut={20} /></span>
          <div>
            <span className="logo-yazi">StokTakip</span>
            <span className="logo-alt-yazi">ENVANTER</span>
          </div>
        </div>
        <nav className="nav">
          {[
            { id: 'panel', ikon: 'panel', yazi: 'Genel Bakış' },
            { id: 'stok', ikon: 'kutu', yazi: 'Stok Listesi' },
            { id: 'islemler', ikon: 'gecmis', yazi: 'İşlem Geçmişi' },
            { id: 'kategoriler', ikon: 'liste', yazi: 'Kategoriler', sadeceAdmin: true },
            { id: 'kullanicilar', ikon: 'kisiler', yazi: 'Kullanıcılar', sadeceAdmin: true },
          ].filter(s => !s.sadeceAdmin || aktifKullanici.rol === 'admin').map(s => (
            <button key={s.id} className={`nav-btn ${aktifSekme === s.id ? 'aktif' : ''}`} onClick={() => setAktifSekme(s.id)}>
              <Ikon ad={s.ikon} boyut={17} /><span className="nav-yazi">{s.yazi}</span>
              {s.id === 'stok' && kritikUrunler.length > 0 && <span className="rozet">{kritikUrunler.length}</span>}
            </button>
          ))}
        </nav>
        <div className="sidebar-alt">
          <div className="kullanici-bilgi">
            <div className="kullanici-avatar">{aktifKullanici.ad.charAt(0).toUpperCase()}</div>
            <div><div className="kullanici-ad">{aktifKullanici.ad}</div><div className="kullanici-rol">{aktifKullanici.rol === 'admin' ? 'Yönetici' : 'Kullanıcı'}</div></div>
          </div>
          <button className="tema-toggle-btn" onClick={() => setTema(t => t === 'dark' ? 'light' : 'dark')} title="Koyu / Açık Tema">
            <span className="tema-rozet"><Ikon ad={tema === 'dark' ? 'gunes' : 'ay'} boyut={14} /> <span className="cikis-yazi">{tema === 'dark' ? 'Açık Tema' : 'Koyu Tema'}</span></span>
          </button>
          <button className="cikis-yap-btn" onClick={cikisYap}><Ikon ad="cik" boyut={15} /><span className="cikis-yazi">Çıkış Yap</span></button>
          <div className="tarih">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        </div>
      </aside>

      {/* İÇERİK */}
      <main className="icerik">
        {bildirim && (
          <div className={`bildirim bildirim-${bildirim.tur}`}>
            {bildirim.mesaj}
          </div>
        )}

        {yukleniyor && <div className="yukleniyor-bar" />}

        {/* ─── GENEL BAKIŞ ──────────────────────────────────────────────────── */}
        {aktifSekme === 'panel' && (
          <div className="sekme-icerik">
            <div className="baslik-satir">
              <div><h1>Genel Bakış</h1><p className="hosgeldin">Hoş geldiniz, <strong>{aktifKullanici.ad}</strong></p></div>
              <div className="buton-grup">
                <button className="btn btn-yesil" onClick={() => acikGirisModal()}><Ikon ad="giris" boyut={15} /> Stok Girişi</button>
                <button className="btn btn-ikincil" onClick={() => acikCikisModal()}><Ikon ad="cikis" boyut={15} /> Stok Çıkışı</button>
                {aktifKullanici.rol === 'admin' && <button className="btn btn-birincil" onClick={() => setUrunModal(true)}><Ikon ad="arti" boyut={15} /> Ürün Ekle</button>}
              </div>
            </div>

            {/* Özet Kartlar — sayım fişleri */}
            <div className="kart-grid">
              {yukleniyor ? (
                <>
                  <OzetKartSkeleton /><OzetKartSkeleton /><OzetKartSkeleton /><OzetKartSkeleton />
                </>
              ) : (
                <>
                  <div className="ozet-kart mavi"><div className="ozet-ust"><span className="ozet-yazi">Toplam Ürün</span><span className="ozet-kod">ÜRN</span></div><div className="ozet-sayi">{urunler.length}</div></div>
                  <div className="ozet-kart yesil"><div className="ozet-ust"><span className="ozet-yazi">Toplam İşlem</span><span className="ozet-kod">İŞL</span></div><div className="ozet-sayi">{islemler.length}</div></div>
                  <div className="ozet-kart turuncu"><div className="ozet-ust"><span className="ozet-yazi">Bugünkü İşlem</span><span className="ozet-kod">BGN</span></div><div className="ozet-sayi">{bugunIslem}</div></div>
                  <div className="ozet-kart mor"><div className="ozet-ust"><span className="ozet-yazi">Benim İşlemlerim</span><span className="ozet-kod">BEN</span></div><div className="ozet-sayi">{benimIslemlerim}</div></div>
                </>
              )}
            </div>

            {/* Son İşlemler */}
            <div className="baslik-satir" style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
              <h2>Son İşlemler</h2>
            </div>
            {!yukleniyor && islemler.length === 0
              ? <div className="bos-durum">Henüz işlem kaydedilmedi</div>
              : <IslemTablosu yukleniyor={yukleniyor} islemler={islemler.slice(0, 10)} aktifKullaniciId={aktifKullanici.id} tarihFormat={tarihFormat} onTimelineAc={(uId) => { const obj = urunler.find(x => x.id === uId); if (obj) setTimelineUrun(obj); }} />
            }

            {/* Kritik uyarı */}
            {kritikUrunler.length > 0 && (
              <div className="uyari-kutu" style={{ marginTop: '1.5rem' }}>
                <h3><Ikon ad="uyari" boyut={16} /> Kritik Stok Seviyesindeki Ürünler</h3>
                <div className="kritik-liste">
                  {kritikUrunler.map(u => (
                    <div key={u.id} className="kritik-satir">
                      <span className="kritik-ad">{u.ad}</span>
                      <span className="kritik-kategori">{u.kategori_ad || '—'}</span>
                      <span className="kritik-miktar">{u.miktar} {u.birim} kaldı</span>
                      <button className="btn btn-yesil btn-kucuk" onClick={() => acikGirisModal(u.id)}>Stok Ekle</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* GRAFİKLER */}
            {islemler.length > 0 && (
              <div className="grafikler-grid">
                {/* Aylık Giriş/Çıkış */}
                <div className="grafik-kart">
                  <h3 className="grafik-baslik">Aylık Giriş / Çıkış</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={aylikVeri(islemler)} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <XAxis dataKey="ay" tick={{ fontSize: 12, fill: '#9AABBC' }} axisLine={{ stroke: '#243140' }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: '#9AABBC' }} axisLine={{ stroke: '#243140' }} tickLine={false} />
                      <Tooltip {...TOOLTIP_STIL} cursor={{ fill: 'rgba(56,189,248,0.06)' }} />
                      <Legend wrapperStyle={{ color: '#9AABBC' }} />
                      <Bar dataKey="giris" name="Giriş" fill="#199E70" radius={[4,4,0,0]} />
                      <Bar dataKey="cikis" name="Çıkış" fill="#C98500" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* En Çok Tüketilen */}
                <div className="grafik-kart">
                  <h3 className="grafik-baslik">En Çok Çıkan 5 Ürün</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={enCokTuketilen(islemler)} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <XAxis type="number" tick={{ fontSize: 12, fill: '#9AABBC' }} axisLine={{ stroke: '#243140' }} tickLine={false} />
                      <YAxis type="category" dataKey="ad" tick={{ fontSize: 11, fill: '#9AABBC' }} width={90} axisLine={{ stroke: '#243140' }} tickLine={false} />
                      <Tooltip {...TOOLTIP_STIL} cursor={{ fill: 'rgba(56,189,248,0.06)' }} />
                      <Bar dataKey="miktar" name="Miktar" fill="#3987E5" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Kişi Dağılımı */}
                {kisiDagilimi(islemler).length > 0 && (
                  <div className="grafik-kart">
                    <h3 className="grafik-baslik">Kişi Bazında Çıkış Dağılımı</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={kisiDagilimi(islemler)} dataKey="deger" nameKey="ad" cx="50%" cy="50%" outerRadius={75} label={false} labelLine={false}>
                          {kisiDagilimi(islemler).map((_, i) => <Cell key={i} fill={PASTA_RENKLER[i % PASTA_RENKLER.length]} stroke="#131B24" strokeWidth={2} />)}
                        </Pie>
                        <Tooltip {...TOOLTIP_STIL} formatter={(val, name) => [`${val} işlem`, name]} />
                        <Legend iconType="square" iconSize={9} formatter={(val) => <span style={{ fontSize: '0.8rem', color: '#9AABBC' }}>{val}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── STOK LİSTESİ ─────────────────────────────────────────────────── */}
        {aktifSekme === 'stok' && (
          <div className="sekme-icerik">
            <div className="baslik-satir">
              <h1>Stok Listesi</h1>
              <div className="buton-grup">
                <button className="btn btn-yesil" onClick={() => acikGirisModal()}><Ikon ad="giris" boyut={15} /> Stok Girişi</button>
                <button className="btn btn-ikincil" onClick={() => acikCikisModal()}><Ikon ad="cikis" boyut={15} /> Stok Çıkışı</button>
                {aktifKullanici.rol === 'admin' && <button className="btn btn-birincil" onClick={() => setUrunModal(true)}><Ikon ad="arti" boyut={15} /> Ürün Ekle</button>}
              </div>
            </div>
            <input className="arama-kutu" placeholder="Ürün veya kategori ara..." value={aramaMetni} onChange={e => { setAramaMetni(e.target.value); setStokSayfa(1); }} />
            {yukleniyor ? (
              <div className="urun-grid">
                {Array.from({ length: 6 }).map((_, i) => <UrunKartSkeleton key={i} />)}
              </div>
            ) : filtreliUrunler.length === 0
              ? <div className="bos-durum">{aramaMetni ? 'Arama sonucu bulunamadı' : 'Henüz ürün eklenmedi.'}</div>
              : (
                <>
                  {urunKategorileri.map(kat => (
                    <div key={kat} className="kategori-grup">
                      <h3 className="kategori-baslik">{kat}</h3>
                      <div className="urun-grid">
                        {filtreliUrunler.filter(u => (u.kategori_ad || 'Genel') === kat).map(u => (
                          <div key={u.id} className={`urun-kart ${u.miktar <= u.kritik_seviye ? 'kritik' : u.miktar <= u.kritik_seviye * 2 ? 'uyari' : ''}`}>
                            <div className="urun-kart-ust">
                              <span className="urun-ad">{u.ad}</span>
                              <div className="kart-ust-aksiyonlar">
                                <button className="fatura-mini-btn" onClick={() => setTimelineUrun(u)} title="Zaman Çizelgesi"><Ikon ad="zaman" boyut={14} /></button>
                                {u.fatura_yolu && <button className="fatura-mini-btn" onClick={() => window.open(api.staticUrl(u.fatura_yolu))} title="Fatura"><Ikon ad="belge" boyut={14} /></button>}
                                <button className="fatura-mini-btn" onClick={() => duzenlemeAc(u)} title="Ürünü Düzenle"><Ikon ad="duzenle" boyut={13} /></button>
                                {aktifKullanici.rol === 'admin' && <button className="sil-btn" onClick={() => setSilinecekUrun(u)} title="Ürünü Sil"><Ikon ad="kapat" boyut={13} /></button>}
                              </div>
                            </div>
                            <div className="urun-miktar"><span className="miktar-sayi">{u.miktar}</span><span className="miktar-birim">{u.birim}</span></div>
                            <div className="seviye-bar"><div className="seviye-dolu" style={{ width: `${Math.min(100, Math.round((u.miktar / Math.max(1, u.kritik_seviye * 3)) * 100))}%` }} /></div>
                            {u.miktar <= u.kritik_seviye && <div className="kritik-uyari">Kritik seviye</div>}
                            <div className="urun-alt">
                              <span className="min-bilgi">MIN {u.kritik_seviye} {u.birim.toUpperCase()}</span>
                              <div className="kart-butonlar">
                                <button className="kart-btn giris-kart-btn" onClick={() => acikGirisModal(u.id)} title="Stok Girişi"><Ikon ad="giris" boyut={14} /></button>
                                <button className="kart-btn cikis-kart-btn" onClick={() => acikCikisModal(u.id)} title="Stok Çıkışı"><Ikon ad="cikis" boyut={14} /></button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )
            }
          </div>
        )}

        {/* ─── İŞLEM GEÇMİŞİ ───────────────────────────────────────────────── */}
        {aktifSekme === 'islemler' && (
          <div className="sekme-icerik">
            <div className="baslik-satir">
              <h1>İşlem Geçmişi</h1>
              <div className="buton-grup">
                <button className="btn btn-excel" onClick={handleExcel}><Ikon ad="tablo" boyut={15} /> Excel İndir</button>
                <button className="btn btn-yesil" onClick={() => acikGirisModal()}><Ikon ad="giris" boyut={15} /> Stok Girişi</button>
                <button className="btn btn-ikincil" onClick={() => acikCikisModal()}><Ikon ad="cikis" boyut={15} /> Stok Çıkışı</button>
              </div>
            </div>

            {/* Filtre Satırı */}
            <div className="filtre-bolum">
              <div className="filtre-satir">
                <input className="arama-kutu" placeholder="Kişi veya ürün ara..." value={islemArama} onChange={e => setIslemArama(e.target.value)} />
                <div className="tip-filtre">
                  {[['hepsi','Hepsi'],['giris','Giriş'],['cikis','Çıkış']].map(([val,yazi]) => (
                    <button key={val} className={`filtre-btn ${islemTip === val ? 'aktif' : ''}`} onClick={() => setIslemTip(val)}>{yazi}</button>
                  ))}
                </div>
              </div>
              <div className="tarih-filtre-satir">
                <div className="tarih-girdi-grup">
                  <label>Başlangıç</label>
                  <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)} />
                </div>
                <div className="tarih-girdi-grup">
                  <label>Bitiş</label>
                  <input type="date" value={bitis} onChange={e => setBitis(e.target.value)} />
                </div>
                <div className="hizli-tarihler">
                  {[['hafta','Bu Hafta'],['ay','Bu Ay'],['3ay','Son 3 Ay'],['temizle','Temizle']].map(([tip,yazi]) => (
                    <button key={tip} className={`hizli-btn ${tip === 'temizle' ? 'temizle' : ''}`} onClick={() => hizliTarih(tip)}>{yazi}</button>
                  ))}
                </div>
              </div>
            </div>

            {!yukleniyor && islemler.length === 0
              ? <div className="bos-durum">Filtre sonucu bulunamadı</div>
              : <IslemTablosu yukleniyor={yukleniyor} islemler={islemler} aktifKullaniciId={aktifKullanici.id} tarihFormat={tarihFormat} numarali onTimelineAc={(uId) => { const obj = urunler.find(x => x.id === uId); if (obj) setTimelineUrun(obj); }} />
            }
          </div>
        )}

        {/* ─── KATEGORİLER ──────────────────────────────────────────────────── */}
        {aktifSekme === 'kategoriler' && aktifKullanici.rol === 'admin' && (
          <div className="sekme-icerik">
            <div className="baslik-satir">
              <h1>Kategoriler</h1>
              <button className="btn btn-birincil" onClick={() => setKategoriModal(true)}><Ikon ad="arti" boyut={15} /> Kategori Ekle</button>
            </div>
            {kategoriler.length === 0 ? (
              <div className="bos-durum">Henüz hiç kategori yok</div>
            ) : (
              <div className="tablo-kap">
                <table className="tablo">
                  <thead><tr><th>Kategori Adı</th><th>Ürün Sayısı</th><th>İşlemler</th></tr></thead>
                  <tbody>
                    {kategoriler.map(k => {
                      const urunSayisi = urunler.filter(u => u.kategori_id === k.id).length;
                      return (
                        <tr key={k.id}>
                          <td>{k.ad}</td>
                          <td>{urunSayisi}</td>
                          <td><div className="eylem-grup">
                            <button className="eylem-btn kirmizi" onClick={() => setSilinecekKategori(k)}><Ikon ad="cop" boyut={13} /> Sil</button>
                          </div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── KULLANICI YÖNETİMİ ───────────────────────────────────────────── */}
        {aktifSekme === 'kullanicilar' && aktifKullanici.rol === 'admin' && (
          <div className="sekme-icerik">
            <div className="baslik-satir">
              <h1>Kullanıcı Yönetimi</h1>
              <button className="btn btn-birincil" onClick={() => setKullaniciModal(true)}><Ikon ad="arti" boyut={15} /> Kullanıcı Ekle</button>
            </div>
            <div className="tablo-kap">
              <table className="tablo">
                <thead><tr><th>Ad Soyad</th><th>Kullanıcı Adı</th><th>Rol</th><th>İşlem Sayısı</th><th>İşlemler</th></tr></thead>
                <tbody>
                  {kullanicilar.map(k => (
                    <tr key={k.id}>
                      <td><div className="kullanici-satir"><div className="mini-avatar">{k.ad.charAt(0).toUpperCase()}</div><span>{k.ad}</span>{k.id === aktifKullanici.id && <span className="ben-etiketi">ben</span>}</div></td>
                      <td><code className="kod-etiket">{k.kullanici_adi}</code></td>
                      <td><span className={`rol-etiketi ${k.rol}`}>{k.rol === 'admin' ? 'Yönetici' : 'Kullanıcı'}</span></td>
                      <td>{islemler.filter(i => i.kullanici_id === k.id).length}</td>
                      <td><div className="eylem-grup">
                        <button className="eylem-btn" onClick={() => { setSifreModal(k); setYeniSifre(''); }}><Ikon ad="anahtar" boyut={13} /> Şifre</button>
                        {k.id !== aktifKullanici.id && <button className="eylem-btn" onClick={() => setRolDegisecek(k)}><Ikon ad="kisiler" boyut={13} /> {k.rol === 'admin' ? 'Kullanıcı Yap' : 'Yönetici Yap'}</button>}
                        {k.id !== aktifKullanici.id && <button className="eylem-btn kirmizi" onClick={() => setSilinecekKullanici(k)}><Ikon ad="cop" boyut={13} /> Sil</button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* ─── MODALLER ─────────────────────────────────────────────────────────── */}

      {/* Ürün Ekle */}
      {urunModal && (
        <div className="modal-arkaplan" onClick={urunModalKapat}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Yeni Ürün Ekle</h2><button className="kapat-btn" onClick={urunModalKapat}><Ikon ad="kapat" boyut={15} /></button></div>
            <div className="form-grup"><label>Ürün Adı *</label><input placeholder="Örn: A4 Kağıt" value={yeniUrun.ad} onChange={e => setYeniUrun(p => ({ ...p, ad: e.target.value }))} autoFocus /></div>
            <KategoriAlani kategoriler={kategoriler} yeniUrun={yeniUrun} setYeniUrun={setYeniUrun} yeniKatModu={yeniKatModu} setYeniKatModu={setYeniKatModu} />
            <div className="form-satir">
              <div className="form-grup"><label>Başlangıç Miktarı *</label><input type="number" min="0" placeholder="0" value={yeniUrun.miktar} onChange={e => setYeniUrun(p => ({ ...p, miktar: e.target.value }))} /></div>
              <div className="form-grup"><label>Birim</label>
                <select value={yeniUrun.birim} onChange={e => setYeniUrun(p => ({ ...p, birim: e.target.value }))}>
                  {['adet','kg','lt','paket','kutu','metre','çift'].map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grup"><label>Kritik Stok Seviyesi</label><input type="number" min="0" placeholder="5" value={yeniUrun.kritik_seviye} onChange={e => setYeniUrun(p => ({ ...p, kritik_seviye: e.target.value }))} /></div>
            <div className="form-grup"><label>Fatura (PDF — isteğe bağlı)</label><PdfYukle value={urunFatura} onDegis={setUrunFatura} onKaldir={() => setUrunFatura(null)} inputId="urun-pdf" /></div>
            <div className="modal-alt"><button className="btn btn-gri" onClick={urunModalKapat}>İptal</button><button className="btn btn-birincil" onClick={urunEkle}>Ekle</button></div>
          </div>
        </div>
      )}

      {/* Stok Girişi */}
      {girisModal && (
        <div className="modal-arkaplan" onClick={girisModalKapat}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2><Ikon ad="giris" boyut={18} /> Stok Girişi</h2><button className="kapat-btn" onClick={girisModalKapat}><Ikon ad="kapat" boyut={15} /></button></div>
            <div className="islem-kisi-bilgi giris-renk">
              <span className="islem-kisi-ikon giris-ikon">{aktifKullanici.ad.charAt(0).toUpperCase()}</span>
              <div><div className="islem-kisi-ad">{aktifKullanici.ad}</div><div className="islem-kisi-aciklama">Stok artışı bu kişi adına kaydedilecek</div></div>
            </div>
            {urunler.length === 0 ? (
              <>
                <div className="bos-durum">{aktifKullanici.rol === 'admin' ? 'Henüz hiç ürün eklenmedi. Stok girişi yapabilmek için önce bir ürün eklemelisiniz.' : 'Henüz hiç ürün eklenmedi. Stok girişi yapabilmek için bir yöneticiden ürün eklemesini isteyin.'}</div>
                <div className="modal-alt">
                  <button className="btn btn-gri" onClick={girisModalKapat}>İptal</button>
                  {aktifKullanici.rol === 'admin' && <button className="btn btn-birincil" onClick={() => { girisModalKapat(); setUrunModal(true); }}><Ikon ad="arti" boyut={15} /> Ürün Ekle</button>}
                </div>
              </>
            ) : (
              <>
                <div className="form-grup"><label>Ürün Kategorisi</label>
                  <select value={yeniGiris.kategori_id} onChange={e => setYeniGiris(p => ({ ...p, kategori_id: e.target.value, urun_id: '' }))}>
                    <option value="">-- Tüm Kategoriler --</option>
                    {kategoriler.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
                <div className="form-grup"><label>Ürün *</label>
                  <select value={yeniGiris.urun_id} onChange={e => setYeniGiris(p => ({ ...p, urun_id: e.target.value }))}>
                    <option value="">-- Ürün Seçin --</option>
                    {urunler.filter(u => !yeniGiris.kategori_id || String(u.kategori_id) === yeniGiris.kategori_id).map(u => <option key={u.id} value={u.id}>{u.ad} (mevcut: {u.miktar} {u.birim})</option>)}
                  </select>
                </div>
                <div className="form-grup"><label>Eklenecek Miktar *</label><input type="number" min="1" placeholder="1" value={yeniGiris.miktar} onChange={e => setYeniGiris(p => ({ ...p, miktar: e.target.value }))} /></div>
                <div className="form-grup"><label>Not (isteğe bağlı)</label><input placeholder="Tedarikçi, fatura no..." value={yeniGiris.not_} onChange={e => setYeniGiris(p => ({ ...p, not_: e.target.value }))} /></div>
                <div className="form-grup"><label>Fatura (PDF — isteğe bağlı)</label><PdfYukle value={girisFatura} onDegis={setGirisFatura} onKaldir={() => setGirisFatura(null)} inputId="giris-pdf" /></div>
                <div className="modal-alt"><button className="btn btn-gri" onClick={girisModalKapat}>İptal</button><button className="btn btn-yesil" onClick={() => stokGiris()}>Stok Ekle</button></div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Stok Çıkışı */}
      {cikisModal && (
        <div className="modal-arkaplan" onClick={cikisModalKapat}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2><Ikon ad="cikis" boyut={18} /> Stok Çıkışı</h2><button className="kapat-btn" onClick={cikisModalKapat}><Ikon ad="kapat" boyut={15} /></button></div>
            <div className="islem-kisi-bilgi">
              <span className="islem-kisi-ikon">{aktifKullanici.ad.charAt(0).toUpperCase()}</span>
              <div><div className="islem-kisi-ad">{aktifKullanici.ad}</div><div className="islem-kisi-aciklama">Bu işlem sizin adınıza kaydedilecek</div></div>
            </div>
            {urunler.length === 0 ? (
              <>
                <div className="bos-durum">{aktifKullanici.rol === 'admin' ? 'Henüz hiç ürün eklenmedi. Stok çıkışı yapabilmek için önce bir ürün eklemelisiniz.' : 'Henüz hiç ürün eklenmedi. Stok çıkışı yapabilmek için bir yöneticiden ürün eklemesini isteyin.'}</div>
                <div className="modal-alt">
                  <button className="btn btn-gri" onClick={cikisModalKapat}>İptal</button>
                  {aktifKullanici.rol === 'admin' && <button className="btn btn-birincil" onClick={() => { cikisModalKapat(); setUrunModal(true); }}><Ikon ad="arti" boyut={15} /> Ürün Ekle</button>}
                </div>
              </>
            ) : (
              <>
                <div className="form-grup"><label>Ürün Kategorisi</label>
                  <select value={yeniCikis.kategori_id} onChange={e => setYeniCikis(p => ({ ...p, kategori_id: e.target.value, urun_id: '' }))}>
                    <option value="">-- Tüm Kategoriler --</option>
                    {kategoriler.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
                <div className="form-grup"><label>Ürün *</label>
                  <select value={yeniCikis.urun_id} onChange={e => setYeniCikis(p => ({ ...p, urun_id: e.target.value }))}>
                    <option value="">-- Ürün Seçin --</option>
                    {urunler.filter(u => !yeniCikis.kategori_id || String(u.kategori_id) === yeniCikis.kategori_id).map(u => <option key={u.id} value={u.id}>{u.ad} ({u.miktar} {u.birim} mevcut)</option>)}
                  </select>
                </div>
                <div className="form-grup"><label>Alınan Miktar *</label><input type="number" min="1" placeholder="1" value={yeniCikis.miktar} onChange={e => setYeniCikis(p => ({ ...p, miktar: e.target.value }))} /></div>
                <div className="form-grup"><label>Çıkış Sebebi *</label><input placeholder="Kullanım yeri, proje, teslim alan kişi veya açıklama yazın..." value={yeniCikis.not_} onChange={e => setYeniCikis(p => ({ ...p, not_: e.target.value }))} /></div>
                <div className="modal-alt"><button className="btn btn-gri" onClick={cikisModalKapat}>İptal</button><button className="btn btn-ikincil" onClick={() => stokCikis()}>Kaydet</button></div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Kullanıcı Ekle */}
      {kullaniciModal && (
        <div className="modal-arkaplan" onClick={() => setKullaniciModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Yeni Kullanıcı Ekle</h2><button className="kapat-btn" onClick={() => setKullaniciModal(false)}><Ikon ad="kapat" boyut={15} /></button></div>
            <div className="form-grup"><label>Ad Soyad *</label><input placeholder="Ahmet Yılmaz" value={yeniKullanici.ad} onChange={e => setYeniKullanici(p => ({ ...p, ad: e.target.value }))} /></div>
            <div className="form-grup"><label>Kullanıcı Adı *</label><input placeholder="ahmet" value={yeniKullanici.kullaniciAdi} onChange={e => setYeniKullanici(p => ({ ...p, kullaniciAdi: e.target.value }))} /></div>
            <div className="form-grup"><label>Şifre *</label><input type="password" placeholder="••••••" value={yeniKullanici.sifre} onChange={e => setYeniKullanici(p => ({ ...p, sifre: e.target.value }))} /></div>
            <div className="form-grup"><label>Rol</label>
              <select value={yeniKullanici.rol} onChange={e => setYeniKullanici(p => ({ ...p, rol: e.target.value }))}>
                <option value="kullanici">Kullanıcı</option>
                <option value="admin">Yönetici</option>
              </select>
            </div>
            <div className="modal-alt"><button className="btn btn-gri" onClick={() => setKullaniciModal(false)}>İptal</button><button className="btn btn-birincil" onClick={handleKullaniciEkle}>Ekle</button></div>
          </div>
        </div>
      )}

      {/* Şifre */}
      {sifreModal && (
        <div className="modal-arkaplan" onClick={() => setSifreModal(null)}>
          <div className="modal modal-kucuk" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Şifre Güncelle</h2><button className="kapat-btn" onClick={() => setSifreModal(null)}><Ikon ad="kapat" boyut={15} /></button></div>
            <p style={{ marginBottom: '1rem', color: 'var(--sessiz)', fontSize: '0.9rem' }}><strong>{sifreModal.ad}</strong> kullanıcısının şifresi değiştirilecek.</p>
            <div className="form-grup"><label>Yeni Şifre *</label><input type="password" placeholder="Yeni şifre girin" value={yeniSifre} onChange={e => setYeniSifre(e.target.value)} autoFocus /></div>
            <div className="modal-alt"><button className="btn btn-gri" onClick={() => setSifreModal(null)}>İptal</button><button className="btn btn-birincil" onClick={handleSifreGuncelle}>Güncelle</button></div>
          </div>
        </div>
      )}

      {/* Ürün Düzenle */}
      {duzenlenecekUrun && (
        <div className="modal-arkaplan" onClick={duzenlemeKapat}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Ürünü Düzenle</h2><button className="kapat-btn" onClick={duzenlemeKapat}><Ikon ad="kapat" boyut={15} /></button></div>
            <div className="form-grup"><label>Ürün Adı *</label><input placeholder="Örn: A4 Kağıt" value={duzenlenecekUrun.ad} onChange={e => setDuzenlenecekUrun(p => ({ ...p, ad: e.target.value }))} autoFocus /></div>
            <KategoriAlani kategoriler={kategoriler} yeniUrun={duzenlenecekUrun} setYeniUrun={setDuzenlenecekUrun} yeniKatModu={duzenleKatModu} setYeniKatModu={setDuzenleKatModu} />
            <div className="modal-alt"><button className="btn btn-gri" onClick={duzenlemeKapat}>İptal</button><button className="btn btn-birincil" onClick={urunGuncelle}>Kaydet</button></div>
          </div>
        </div>
      )}

      {/* Ürün Sil */}
      {silinecekUrun && (
        <div className="modal-arkaplan" onClick={() => setSilinecekUrun(null)}>
          <div className="modal modal-kucuk" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Ürünü Sil</h2><button className="kapat-btn" onClick={() => setSilinecekUrun(null)}><Ikon ad="kapat" boyut={15} /></button></div>
            <p style={{ margin: '1rem 0' }}><strong>"{silinecekUrun.ad}"</strong> ürünü, tüm işlem geçmişiyle birlikte silinecek. Emin misiniz?</p>
            <div className="modal-alt"><button className="btn btn-gri" onClick={() => setSilinecekUrun(null)}>İptal</button><button className="btn btn-kirmizi" onClick={() => urunSil(silinecekUrun.id)}>Sil</button></div>
          </div>
        </div>
      )}

      {/* Rol Değiştir */}
      {rolDegisecek && (
        <div className="modal-arkaplan" onClick={() => setRolDegisecek(null)}>
          <div className="modal modal-kucuk" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Rol Değiştir</h2><button className="kapat-btn" onClick={() => setRolDegisecek(null)}><Ikon ad="kapat" boyut={15} /></button></div>
            <p style={{ margin: '1rem 0' }}>
              <strong>"{rolDegisecek.ad}"</strong>{' '}
              {rolDegisecek.rol === 'admin'
                ? 'yönetici yetkilerini kaybedecek ve normal kullanıcı olacak.'
                : 'yönetici yetkilerine sahip olacak: ürün silebilecek ve kullanıcıları yönetebilecek.'}{' '}
              Emin misiniz?
            </p>
            <div className="modal-alt">
              <button className="btn btn-gri" onClick={() => setRolDegisecek(null)}>İptal</button>
              <button className="btn btn-birincil" onClick={handleRolDegistir}>{rolDegisecek.rol === 'admin' ? 'Kullanıcı Yap' : 'Yönetici Yap'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Sil */}
      {silinecekKullanici && (
        <div className="modal-arkaplan" onClick={() => setSilinecekKullanici(null)}>
          <div className="modal modal-kucuk" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Kullanıcıyı Sil</h2><button className="kapat-btn" onClick={() => setSilinecekKullanici(null)}><Ikon ad="kapat" boyut={15} /></button></div>
            <p style={{ margin: '1rem 0' }}><strong>"{silinecekKullanici.ad}"</strong> kullanıcısını silmek istediğinizden emin misiniz?</p>
            <div className="modal-alt"><button className="btn btn-gri" onClick={() => setSilinecekKullanici(null)}>İptal</button><button className="btn btn-kirmizi" onClick={() => handleKullaniciSil(silinecekKullanici.id)}>Sil</button></div>
          </div>
        </div>
      )}

      {/* Kategori Ekle */}
      {kategoriModal && (
        <div className="modal-arkaplan" onClick={kategoriModalKapat}>
          <div className="modal modal-kucuk" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Yeni Kategori Ekle</h2><button className="kapat-btn" onClick={kategoriModalKapat}><Ikon ad="kapat" boyut={15} /></button></div>
            <div className="form-grup">
              <label>Kategori Adı *</label>
              <input placeholder="Örn: Ofis Malzemeleri" value={yeniKategoriAdi} onChange={e => setYeniKategoriAdi(e.target.value)} autoFocus onKeyDown={e => { if (e.key === 'Enter') kategoriEkle(); }} />
            </div>
            <div className="modal-alt"><button className="btn btn-gri" onClick={kategoriModalKapat}>İptal</button><button className="btn btn-birincil" onClick={kategoriEkle}>Ekle</button></div>
          </div>
        </div>
      )}

      {/* Kategori Sil */}
      {silinecekKategori && (
        <div className="modal-arkaplan" onClick={() => setSilinecekKategori(null)}>
          <div className="modal modal-kucuk" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik"><h2>Kategoriyi Sil</h2><button className="kapat-btn" onClick={() => setSilinecekKategori(null)}><Ikon ad="kapat" boyut={15} /></button></div>
            <p style={{ margin: '1rem 0' }}><strong>"{silinecekKategori.ad}"</strong> kategorisini silmek istediğinizden emin misiniz?</p>
            <div className="modal-alt"><button className="btn btn-gri" onClick={() => setSilinecekKategori(null)}>İptal</button><button className="btn btn-kirmizi" onClick={() => kategoriSil(silinecekKategori.id)}>Sil</button></div>
          </div>
        </div>
      )}

      {/* Duplikat İşlem Uyarısı */}
      {duplikatUyari && (
        <div className="modal-arkaplan" onClick={() => setDuplikatUyari(null)}>
          <div className="modal modal-kucuk" onClick={e => e.stopPropagation()}>
            <div className="modal-baslik">
              <h2><Ikon ad="uyari" boyut={18} /> Mükerrer İşlem</h2>
              <button className="kapat-btn" onClick={() => setDuplikatUyari(null)}><Ikon ad="kapat" boyut={15} /></button>
            </div>
            <div className="duplikat-uyari-icerik">
              <p>{duplikatUyari.mesaj}</p>
              <p className="duplikat-alt-yazi">Yine de kaydetmek istiyor musunuz?</p>
            </div>
            <div className="modal-alt">
              <button className="btn btn-gri" onClick={() => setDuplikatUyari(null)}>İptal</button>
              <button className="btn btn-turuncu" onClick={duplikatUyari.onay}>Evet, Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Modal */}
      {timelineUrun && <TimelineModal urun={timelineUrun} onClose={() => setTimelineUrun(null)} tarihFormat={tarihFormat} />}
    </div>
  );
}

// ── İşlem Tablosu (paylaşılan bileşen) ───────────────────────────────────────
function IslemTablosu({ islemler, aktifKullaniciId, tarihFormat, numarali, onTimelineAc, yukleniyor }) {
  return (
    <div className="tablo-kap">
      <table className="tablo">
        <thead>
          <tr>
            {numarali && <th>#</th>}
            <th>Tarih & Saat</th><th>Tür</th><th>Kişi</th><th>Ürün</th><th>Miktar</th><th>Not</th><th>Fatura</th>
          </tr>
        </thead>
        <tbody>
          {yukleniyor ? (
            <TabloSatirSkeleton kolonSayisi={numarali ? 8 : 7} satirSayisi={5} />
          ) : islemler.map((i, idx) => (
            <tr key={i.id} className={i.kullanici_id === aktifKullaniciId ? 'benim-satirim' : ''}>
              {numarali && <td className="sira">{islemler.length - idx}</td>}
              <td className="tarih-hucre">{tarihFormat(i.tarih)}</td>
              <td><span className={`tip-etiketi ${i.tip === 'giris' ? 'giris' : 'cikis'}`}>{i.tip === 'giris' ? 'Giriş' : 'Çıkış'}</span></td>
              <td><span className={`kisi-etiketi ${i.kullanici_id === aktifKullaniciId ? 'ben' : ''}`}>{i.kullanici_ad}</span></td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ fontWeight: 600 }}>{i.urun_ad}</span>
                  {onTimelineAc && (
                    <button className="fatura-mini-btn" onClick={() => onTimelineAc(i.urun_id)} title="Ürün Zaman Çizelgesi">
                      <Ikon ad="zaman" boyut={13} />
                    </button>
                  )}
                </div>
              </td>
              <td><span className={`miktar-etiket ${i.tip === 'giris' ? 'giris' : 'cikis'}`}>{i.tip === 'giris' ? '+' : '−'}{i.miktar} {i.birim}</span></td>
              <td className="not-hucre">{i.not_ || '—'}</td>
              <td>{i.fatura_yolu ? <button className="fatura-btn" onClick={() => window.open(api.staticUrl(i.fatura_yolu))} title="Faturayı Görüntüle"><Ikon ad="belge" boyut={12} /> PDF</button> : <span className="yok">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function App() {
  const { aktifKullanici } = useAuth();
  return aktifKullanici ? <Dashboard /> : <Login />;
}
